import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Palette, UploadSimple, Trash, FloppyDisk, LinkSimple, Copy, ArrowSquareOut, PencilSimple, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import BrandPreviewDialog from "./BrandPreviewDialog";
import { isHexColor, normaliseHex } from "../lib/brandTheme";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { company_name: "", tagline: "", logo_url: "", footer_text: "", primary_color: "", logo_public_path: "", portal_slug: "" };
const DEFAULT_COLOR = "#1D4ED8";

/* Admin: per-client white-label identity used across the client portal, client PDFs and client-facing emails. */
export default function ClientBrandingPanel({ clientId, clientName = "" }) {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editSlug, setEditSlug] = useState(false);
  const [slugDraft, setSlugDraft] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileRef = useRef(null);

  const [saved, setSaved] = useState(null);
  const load = () => API.get(`/clients/${clientId}/branding`).then(({ data }) => { setB({ ...EMPTY, ...data }); setSaved({ ...EMPTY, ...data }); }).catch(() => setB(EMPTY));
  const DRAFT_KEYS = ["company_name", "tagline", "footer_text", "primary_color"];
  const draft = b && !b.inherited_from ? Object.fromEntries(DRAFT_KEYS.map((k) => [k, b[k] || ""])) : null;
  const dirty = !!(draft && saved && DRAFT_KEYS.some((k) => (b[k] || "") !== (saved[k] || "")));
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      if (b.primary_color && !isHexColor(b.primary_color)) { toast.error("Brand colour must be a hex value like #1D4ED8"); setSaving(false); return; }
      const { data } = await API.put(`/clients/${clientId}/branding`, { company_name: b.company_name, tagline: b.tagline, logo_url: b.logo_url, footer_text: b.footer_text, primary_color: normaliseHex(b.primary_color) });
      toast.success("Portal branding saved");
      if (data?.portal_slug) setB((p) => ({ ...p, portal_slug: data.portal_slug }));
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setSaving(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post(`/clients/${clientId}/branding/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setB((p) => ({ ...p, logo_url: data.logo_url, logo_public_path: data.logo_public_path, portal_slug: data.portal_slug || p.portal_slug }));
      toast.success("Logo uploaded");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Upload failed"); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    try {
      await API.put(`/clients/${clientId}/branding`, { logo_url: "" });
      setB((p) => ({ ...p, logo_url: "", logo_public_path: "" }));
      toast.success("Logo removed");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const saveSlug = async () => {
    try {
      const { data } = await API.put(`/clients/${clientId}/branding`, { portal_slug: slugDraft });
      setB((p) => ({ ...p, portal_slug: data.portal_slug }));
      setEditSlug(false);
      toast.success("Sign-in link updated");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  if (!b) return null;
  const active = !!(b.company_name || b.logo_url);
  const logoSrc = b.logo_public_path ? `${BACKEND}${b.logo_public_path}` : "";
  const loginUrl = b.portal_slug ? `${window.location.origin}/login/${b.portal_slug}` : "";
  const copyLink = () => { navigator.clipboard?.writeText(loginUrl); toast.success("Sign-in link copied"); };

  return (
    <div className="mb-6 border border-border rounded-lg overflow-hidden" data-testid="client-branding-section">
      <div className="flex items-center justify-between p-4 bg-muted/20">
        <div className="flex items-center gap-3">
          <Palette size={20} className="text-primary" />
          <div>
            <h2 className="text-sm font-semibold font-[Chivo]">Portal Branding (White-Label)</h2>
            <p className="text-[10px] text-muted-foreground mt-0.5">Logo and name shown to this client's users across their portal, quote/invoice PDFs and client-facing emails. Leave blank to show your company branding.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" variant="outline" onClick={() => setPreviewOpen(true)} data-testid="client-branding-preview-btn"><Eye size={14} className="mr-1" /> Preview portal, email &amp; PDF</Button>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${b.inherited_from ? "bg-violet-500/10 text-violet-700 dark:text-violet-300" : active ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400" : "bg-muted text-muted-foreground"}`} data-testid="client-branding-status">{b.inherited_from ? "Inherited from main client" : active ? "Custom brand" : "Default branding"}</span>
        </div>
      </div>
      <BrandPreviewDialog clientId={clientId} clientName={clientName} open={previewOpen} onOpenChange={setPreviewOpen} draft={draft} dirty={dirty} />
      {b.inherited_from ? (
        <div className="p-4 flex flex-wrap items-center gap-4" data-testid="client-branding-inherited">
          <div className="h-16 w-40 rounded-md border border-dashed border-border bg-muted/30 flex items-center justify-center overflow-hidden shrink-0">
            {b.effective?.logo_public_path ? <img src={`${BACKEND}${b.effective.logo_public_path}`} alt="Main client logo" className="max-h-14 max-w-[150px] object-contain" /> : <span className="text-[11px] text-muted-foreground">{b.effective?.company_name || "your company branding"}</span>}
          </div>
          {b.effective?.primary_color && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0" data-testid="client-branding-inherited-color">
              <span className="h-6 w-6 rounded-full border border-border shadow-sm" style={{ backgroundColor: b.effective.primary_color }} />
              <span className="font-mono">{b.effective.primary_color}</span>
            </div>
          )}
          <div className="text-sm min-w-0 flex-1">
            <p>This is a sub-client of <Link to={`/clients/${b.inherited_from.id}`} className="font-semibold text-primary hover:underline" data-testid="client-branding-inherited-link">{b.inherited_from.name}</Link>, so its users always see <strong>{b.effective?.company_name || "your company branding"}</strong> — on the portal, PDFs, emails and the branded sign-in page.</p>
            <p className="text-xs text-muted-foreground mt-1">To change it, edit the branding on the main client. Detach this client on the "Main client" panel above to give it its own branding again.</p>
            {loginUrl && <p className="text-xs mt-1.5 font-mono text-muted-foreground truncate">Sign-in link: {loginUrl}</p>}
          </div>
        </div>
      ) : (<>
      <div className="p-4 grid md:grid-cols-[180px_1fr] gap-5">
        <div>
          <Label className="text-xs">Logo</Label>
          <div className="mt-1.5 h-24 rounded-md border border-dashed border-border bg-muted/30 flex items-center justify-center overflow-hidden" data-testid="client-branding-logo-preview">
            {logoSrc ? <img src={logoSrc} alt="Client logo" className="max-h-20 max-w-[160px] object-contain" /> : <span className="text-[11px] text-muted-foreground">No logo</span>}
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="client-branding-logo-input" />
          <div className="flex gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="client-branding-upload-btn"><UploadSimple size={14} className="mr-1" /> {uploading ? "Uploading…" : b.logo_url ? "Replace" : "Upload"}</Button>
            {b.logo_url && <Button size="sm" variant="ghost" className="text-destructive" onClick={removeLogo} data-testid="client-branding-remove-logo"><Trash size={14} /></Button>}
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">PNG, JPG, WEBP or SVG · max 3MB · transparent background works best</p>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Display name</Label>
            <Input value={b.company_name} onChange={(e) => setB({ ...b, company_name: e.target.value })} placeholder="e.g. Wilson Property Management" className="mt-1" data-testid="client-branding-name" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Tagline (PDFs)</Label>
              <Input value={b.tagline} onChange={(e) => setB({ ...b, tagline: e.target.value })} placeholder="Optional" className="mt-1" data-testid="client-branding-tagline" />
            </div>
            <div>
              <Label className="text-xs">PDF footer text</Label>
              <Input value={b.footer_text} onChange={(e) => setB({ ...b, footer_text: e.target.value })} placeholder="Optional" className="mt-1" data-testid="client-branding-footer" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Brand colour (portal, emails &amp; PDFs)</Label>
            <div className="mt-1 flex items-center gap-2">
              <input type="color" value={isHexColor(b.primary_color) ? normaliseHex(b.primary_color) : DEFAULT_COLOR} onChange={(e) => setB({ ...b, primary_color: e.target.value.toUpperCase() })} className="h-9 w-12 rounded-md border border-input bg-background p-1 cursor-pointer" aria-label="Pick brand colour" data-testid="client-branding-color-picker" />
              <Input value={b.primary_color || ""} onChange={(e) => setB({ ...b, primary_color: e.target.value })} placeholder={`${DEFAULT_COLOR} (default)`} className="w-36 font-mono uppercase" maxLength={7} data-testid="client-branding-color" />
              {b.primary_color && <Button size="sm" variant="ghost" onClick={() => setB({ ...b, primary_color: "" })} data-testid="client-branding-color-reset">Reset</Button>}
              <span className="text-[11px] text-muted-foreground">Sub-clients inherit this colour too.</span>
            </div>
          </div>
          <div className="flex items-center justify-between pt-1">
            <p className="text-[11px] text-muted-foreground">Preview: <span className="font-semibold text-foreground" data-testid="client-branding-preview-name">{b.company_name || "Your company"}</span> — Client Portal</p>
            <Button size="sm" onClick={save} disabled={saving} data-testid="client-branding-save"><FloppyDisk size={14} className="mr-1" /> {saving ? "Saving…" : "Save Branding"}</Button>
          </div>
        </div>
      </div>
      <div className="border-t border-border px-4 py-3 bg-muted/10" data-testid="client-branding-link-row">
        <div className="flex items-center gap-2 mb-1">
          <LinkSimple size={14} className="text-primary" />
          <p className="text-xs font-semibold">Branded sign-in link</p>
          <span className="text-[10px] text-muted-foreground">— shows this client's logo and name before they log in. Share it in onboarding or bookmark on site.</span>
        </div>
        {!active ? (
          <p className="text-[11px] text-muted-foreground" data-testid="client-branding-link-hint">Save a display name or logo first — the link is generated automatically.</p>
        ) : editSlug ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-muted-foreground">{window.location.origin}/login/</span>
            <Input value={slugDraft} onChange={(e) => setSlugDraft(e.target.value.toLowerCase())} className="h-8 text-xs font-mono w-56" placeholder="wilson-property" data-testid="client-branding-slug-input" />
            <Button size="sm" onClick={saveSlug} disabled={!slugDraft.trim()} data-testid="client-branding-slug-save">Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditSlug(false)} data-testid="client-branding-slug-cancel">Cancel</Button>
            <span className="text-[10px] text-muted-foreground">3-40 chars: lowercase letters, numbers, hyphens</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-xs bg-muted px-2 py-1 rounded font-mono break-all" data-testid="client-branding-login-url">{loginUrl || "Generating…"}</code>
            {loginUrl && <>
              <Button size="sm" variant="outline" onClick={copyLink} data-testid="client-branding-copy-link"><Copy size={13} className="mr-1" /> Copy</Button>
              <Button size="sm" variant="ghost" asChild><a href={loginUrl} target="_blank" rel="noreferrer" data-testid="client-branding-open-link"><ArrowSquareOut size={13} className="mr-1" /> Preview</a></Button>
              <Button size="sm" variant="ghost" onClick={() => { setSlugDraft(b.portal_slug); setEditSlug(true); }} data-testid="client-branding-edit-slug"><PencilSimple size={13} className="mr-1" /> Customise</Button>
            </>}
          </div>
        )}
      </div>
      </>)}
    </div>
  );
}
